import React, { useMemo } from 'react'
import { NavLink } from 'react-router'
import { FormattedMessage, useIntl } from 'react-intl'
import { Flex } from 'rebass/styled-components'

import Chevron from '../../Icon/Chevron'
import styles from './NavGroup.module.css'

export interface GroupHeadingProps {
  hasActiveChild: boolean
  icon: React.ComponentType
  labelId: string
  navExpanded: boolean
  showExpandedNavContents: boolean
  teamName: string
  to: string
  isExpanded?: boolean
  onToggle?: () => void
}

export const GroupHeading = ({
  hasActiveChild,
  icon: Icon,
  labelId,
  navExpanded,
  showExpandedNavContents,
  teamName,
  to,
  isExpanded = false,
  onToggle
}: GroupHeadingProps): JSX.Element => {
  const intl = useIntl()

  const handleClick = (e: React.MouseEvent) => {
    // Only handle toggle click if nav is expanded and onToggle is provided
    if (navExpanded && onToggle) {
      e.preventDefault()
      onToggle()
    }
  }

  // Get the section label for accessibility
  const sectionLabelId = `${labelId}-heading`
  const sectionContentId = `${labelId}-content`

  const iconContent = useMemo(
    () => (
      <Flex
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          width: '38px',
          height: '40px',
          borderRadius: '3px',
          color: 'white',
          backgroundColor: hasActiveChild ? 'blue500' : 'transparent'
        }}
        aria-hidden={navExpanded}
      >
        <Icon aria-hidden="true" />
      </Flex>
    ),
    [hasActiveChild, navExpanded, Icon]
  )

  // Render collapsed navigation view (using NavLink for keyboard navigation to display hover menu)
  const renderCollapsedView = () => (
    <NavLink
      to={to}
      className={`${styles.collapsedNavLink} ${hasActiveChild ? styles.active : ''}`}
      aria-label={intl.formatMessage({ id: `${labelId}.label` }, { teamName })}
    >
      {iconContent}
    </NavLink>
  )

  // Render expanded navigation view (using button for accessibility with keyboard expand/collapse controls)
  const renderExpandedView = () => (
    <button
      type="button"
      onClick={handleClick}
      className={`${styles.groupHeadingWrapper} ${hasActiveChild ? styles.active : ''}`}
      role="button"
      aria-expanded={onToggle ? isExpanded : undefined}
      aria-controls={onToggle ? sectionContentId : undefined}
      aria-labelledby={sectionLabelId}
      onKeyDown={e => {
        if (onToggle && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onToggle()
        }
      }}
    >
      <Flex
        sx={{
          flex: 'auto 0',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '35px'
        }}
      >
        <Icon aria-hidden="true" />
      </Flex>
      {showExpandedNavContents && (
        <>
          <p className={styles.groupHeadingText} id={sectionLabelId}>
            <FormattedMessage id={`${labelId}.label`} values={{ teamName }} />
          </p>
          {onToggle && <Chevron width={12} className={`${styles.chevron} ${isExpanded ? styles.expanded : ''}`} />}
        </>
      )}
    </button>
  )

  // Return the appropriate view based on nav state
  return !navExpanded ? renderCollapsedView() : renderExpandedView()
}
